import { Form, Input } from "antd"
import { FormSkeleton } from "components/new-stuff/ui/Skeleton"

import { Button, DashboardContainer, H5, P2 } from "components"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { CustomInput, CustomSelect } from "components/new-stuff/form"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { IState, ICountry } from "pages/dashboard/people/people/typings"
import { IOfficeAddress } from "./typings"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import getIdFromKey from "utils/getIdFromKey"
import FormSectionWrapper from "components/new-stuff/form/FormSectionWrapper"
import GeoPicker from "components/GeoPicker"
import { parseGooglePlace } from "utils/googleAddress"
import { companyService, COMPANY_ENDPOINTS } from "services/companyService"
import { commonService, COMMON_ENDPOINTS } from "services/commonService"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores"
import { SSNIT_INSTITUTION_OPTIONS } from "./AddWorkplaceForm"

const EditWorkplaceForm = () => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [alpha2Code, setAlpha2Code] = useState<string>("NG")
  const [stateCode, setStateCode] = useState<string>("")
  const navigate = useNavigate()
  const params = useParams()
  const id = getIdFromKey(params.id as string)
  const { toast } = useAppNotificationStore()

  const [pendingState, setPendingState] = useState<{
    name: string
    short: string
  } | null>(null)

  // Existing coordinates are seeded from the record (see initialValues), so an
  // unchanged address keeps its lat/lng. If the record has no coordinates the
  // field prompts for a Google address and Save stays disabled until one is picked.

  const { isLoading: isAddressesLoading, data: addressesRes } = useQuery<
    IBaseRes<IOfficeAddress[]>
  >({
    queryKey: [COMPANY_ENDPOINTS.GET_OFFICE_ADDRESSES, { canAdd: true }],
    queryFn: () => companyService.getOfficeAddresses({ canAdd: true }),
    onSuccess: (data) => {
      const address = data?.data?.find(
        (item) => item.officeAddressId.toString() === id
      )

      setStateCode(address?.stateCode || "")
      setAlpha2Code(address?.countryCode || "")
    },
  })

  const addresses = addressesRes?.data

  const address = addresses?.find(
    (item) => item.officeAddressId.toString() === id
  )

  const latitude = Form.useWatch("latitude", form)
  const longitude = Form.useWatch("longitude", form)
  const hasCoordinates =
    latitude != null && latitude !== "" && longitude != null && longitude !== ""

  const { isLoading: isCountriesLoading, data: countriesRes } = useQuery<
    IBaseRes<ICountry[]>
  >({
    queryKey: [COMMON_ENDPOINTS.GET_COUNTRIES],
    queryFn: () => commonService.getCountries(),
  })
  const countries = countriesRes?.data

  const {
    isLoading: isStatesLoading,
    data: statesRes,
    refetch,
  } = useQuery<IBaseRes<IState[]>>({
    queryKey: [COMMON_ENDPOINTS.GET_STATES_BY_COUNTRY, alpha2Code],
    queryFn: () => commonService.getStatesByCountry(alpha2Code),
    enabled: !!alpha2Code,
  })
  const states = statesRes?.data

  const {
    isLoading: isTaxOfficesLoading,
    data: taxOfficesRes,
    refetch: refetchTaxOffices,
  } = useQuery<IBaseRes<{ description: string; code: string }[]>>({
    queryKey: [COMPANY_ENDPOINTS.GET_TAX_OFFICES, stateCode],
    queryFn: () => companyService.getTaxOffices(stateCode),
    enabled: !!stateCode,
  })
  const taxOffices = taxOfficesRes?.data

  const {
    isLoading: isHousingLocationsLoading,
    data: housingLocationsRes,
    refetch: refetchHousingLocationsOffices,
  } = useQuery<IBaseRes<{ description: string; code: string }[]>>({
    queryKey: [COMPANY_ENDPOINTS.GET_HOUSING_LOCATIONS, stateCode],
    queryFn: () => companyService.getHousingLocations(stateCode),
    enabled: !!stateCode,
  })
  const housingLocations = housingLocationsRes?.data

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: (body: any) => companyService.updateOfficeAddress(body),
    onSuccess: (data) => {
      toast.success(data?.message)
      queryClient.invalidateQueries({
        queryKey: [COMPANY_ENDPOINTS.GET_OFFICE_ADDRESSES],
      })
      navigate("/dashboard/company-details/manage-workplace")
      mixPanelEvent("company-edit-workplace-success")
    },
    onError: onQueryError,
  })

  const { isLoading: isEnablingAddress, mutate: enableAddress } = useMutation({
    mutationFn: () => companyService.enableOfficeAddress(id),
    onSuccess: (data) => {
      toast.success(data?.message)
      queryClient.invalidateQueries({
        queryKey: [COMPANY_ENDPOINTS.GET_OFFICE_ADDRESSES],
      })
      navigate("/dashboard/company-details/manage-workplace")
    },
    onError: onQueryError,
  })

  const { isLoading: isDisablingAddress, mutate: disableAddress } = useMutation(
    {
      mutationFn: () => companyService.disableOfficeAddress(id),
      onSuccess: (data) => {
        toast.success(data?.success)
        queryClient.invalidateQueries({
          queryKey: [COMPANY_ENDPOINTS.GET_OFFICE_ADDRESSES],
        })
        navigate("/dashboard/company-details/manage-workplace")
      },
      onError: onQueryError,
    }
  )

  // Once the states for the picked country arrive, match Google's state to a
  // dropdown option and cascade the tax office / NHF lookups.
  useEffect(() => {
    if (!pendingState || !states?.length) return
    const match = states.find(
      (s) =>
        s.description?.toLowerCase() === pendingState.name.toLowerCase() ||
        s.code?.toLowerCase() === pendingState.short.toLowerCase()
    )
    if (match) {
      form.setFieldsValue({ state: match.description })
      setStateCode(match.code)

      refetchTaxOffices()
      refetchHousingLocationsOffices()

      setPendingState(null)
    }
  }, [states, pendingState])

  const handlePlaceDetails = (place: any) => {
    const parsed = parseGooglePlace(place)

    const country = countries?.find((c) => c.alpha2Code === parsed.countryCode)

    // A new address changes the state, so reset the state-dependent downstream
    // fields (tax office / NHF); the effect re-derives them for the new state.
    form.setFieldsValue({
      addressLine1: parsed.addressLine1,
      city: parsed.city || form.getFieldValue("city"),
      zip: parsed.zip || form.getFieldValue("zip"),
      taxOffice: "",
      housingLocation: "",
      ...(country ? { country: country.description } : {}),
    })

    if (country && country.alpha2Code !== alpha2Code) {
      setAlpha2Code(country.alpha2Code)
      setStateCode("")
      form.setFieldsValue({ state: "" })
      refetch()
    }

    if (parsed.stateName || parsed.stateShortName) {
      setPendingState({ name: parsed.stateName, short: parsed.stateShortName })
    }

    form.validateFields(["addressLine1"]).catch(() => {})
  }

  if (isAddressesLoading) {
    return <FormSkeleton fields={8} />
  }

  const onFinish = (values: any) => {
    const { addressLine1, addressLine2, city, zip, phone, displayName, taxId } =
      values
    const item = states?.find((state) => state.description === values.state)
    const ctry = countries?.find(
      (country) => country.description === values.country
    )
    const taxOffice = taxOffices?.find(
      (taxOffice) => taxOffice.description === values.taxOffice
    )

    const nhf = housingLocations?.find(
      (housingLocation) =>
        housingLocation.description === values.housingLocation
    )
    submit({
      office_address_id: id,
      edit_office_address_line_1: addressLine1,
      edit_office_address_line_2: addressLine2,
      edit_office_address_city: city,
      edit_office_address_state_code: item?.code,
      edit_office_address_zip_code: zip,
      edit_office_address_country_code: ctry?.alpha2Code,
      edit_office_address_phone: phone,
      edit_display_name: displayName,
      edit_tax_office_name: taxOffice?.description,
      edit_tax_office_code: taxOffice?.code,
      edit_tax_id: taxId,
      edit_housing_location_name: nhf?.description || "",
      edit_housing_location_code: nhf?.code || "",
      edit_latitude: values.latitude != null ? String(values.latitude) : "",
      edit_longitude: values.longitude != null ? String(values.longitude) : "",
      au_comment: "",
      ssnit_institution_name:
        SSNIT_INSTITUTION_OPTIONS.find(
          (option) => option.value === values.institution
        )?.label || "",
      ssnit_institution_code: values.institution || "",
    })
    mixPanelEvent("company-edit-workplace-init")
  }

  return (
    <DashboardContainer>
      <FormSectionWrapper
        isActive
        title={`Edit workplace `}
        subtitle="To automate your payroll filings, we need to have your company’s
            accurate addresses. Please take a minute and enter them below."
        footerBtns={
          <div className="flex justify-between items-center gap-2">
            {address?.isActive ? (
              <button
                type="button"
                onClick={() => disableAddress()}
                className="text-[#F41418] font-avenir text-sm font-bold disabled:opacity-50"
                disabled={isDisablingAddress}
              >
                Deactivate
              </button>
            ) : (
              <button
                type="button"
                onClick={() => enableAddress()}
                className="text-[#F41418] font-avenir text-sm font-bold disabled:opacity-50"
                disabled={isEnablingAddress}
              >
                Activate
              </button>
            )}

            <div className="flex justify-end w-full gap-3">
              <Button
                color="neutral"
                onClick={(e) => {
                  e.preventDefault()
                  navigate("/dashboard/company-details/manage-workplace")
                }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={isSubmitting || !hasCoordinates}
                submitting={isSubmitting}
              >
                Save
              </Button>
            </div>
          </div>
        }
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{
          addressLine1: address?.addressLine1,
          addressLine2: address?.addressLine2,
          city: address?.addressCity,
          state: address?.addressState,
          zip: address?.zipCode,
          country: address?.country,
          taxOffice: address?.taxOfficeName,
          housingLocation: address?.housingLocationName,
          phone: address?.phone,
          displayName: address?.displayName,
          taxId: address?.taxId,
          latitude: address?.latitude,
          longitude: address?.longitude,
          institution: address?.ssnitInstitutionName || "",
        }}
      >
        <div className="px-4 md:px-6 xl:px-[60px]">
          <div className="bg-[#FFF6E6] border-l-8 border-[#F39F03] text-[##42526D] py-2.5 px-2 mb-6 ">
            Remember to update your filing and mailing address with relevant
            government authorities. This is your current filing and mailing
            address, so remember to update your filing and mailing address with
            relevant government authorities. To deactivate this workplace, use
            another workplace as your filing and mailing address.
          </div>

          <div className="flex flex-col lg:flex-row lg:gap-10 ">
            <div className="w-full lg:w-1/2">
              <Form.Item
                label={
                  <span className="text-padeBlack font-circular text-sm">
                    Address line 1
                  </span>
                }
                name="addressLine1"
                rules={[{ required: true, message: "Please enter this field" }]}
                validateStatus={!hasCoordinates ? "error" : undefined}
                help={
                  !hasCoordinates
                    ? "Please search and select your workplace address from the suggestions to continue"
                    : undefined
                }
              >
                <GeoPicker
                  onSelect={(lat, lng) => {
                    form.setFieldsValue({ latitude: lat, longitude: lng })
                    form.validateFields(["addressLine1"]).catch(() => {})
                  }}
                  onPlaceDetails={handlePlaceDetails}
                />
              </Form.Item>
              <Form.Item
                name="latitude"
                hidden
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="longitude"
                hidden
              >
                <Input />
              </Form.Item>
              <CustomInput
                label="Address line 2"
                name="addressLine2"
              />
              <CustomInput
                label="City"
                name="city"
              />
              {alpha2Code === "GH" && (
                <CustomSelect
                  label="SSNIT Institution"
                  name="institution"
                  required
                  hidden={!hasCoordinates}
                  options={SSNIT_INSTITUTION_OPTIONS}
                  placeholder="Please select"
                />
              )}
              {/* country */}
              <CustomSelect
                label="Country"
                name="country"
                required
                onChange={(selected) => {
                  const country = countries?.find(
                    (country) => country.description === selected
                  )
                  setPendingState(null)
                  setAlpha2Code(country?.alpha2Code || "")
                  form.setFieldsValue({
                    state: "",
                    taxOffice: "",
                    housingLocation: "",
                    city: "",
                    zip: "",
                  })
                  refetch()
                }}
                isLoading={isCountriesLoading}
                options={
                  isCountriesLoading
                    ? []
                    : countries?.map((state) => ({
                        label: state.description,
                        value: state.description,
                      }))
                }
                placeholder="Please select"
              />
              {/* ---- States ---- */}
              <CustomSelect
                label="State/Province"
                name="state"
                required
                onChange={(selected) => {
                  const state = states?.find(
                    (state) => state.description === selected
                  )
                  setPendingState(null)
                  setStateCode(state?.code || "")
                  form.setFieldsValue({
                    taxOffice: "",
                    housingLocation: "",
                    city: "",
                    zip: "",
                  })
                  refetchTaxOffices()
                  refetchHousingLocationsOffices()
                }}
                isLoading={isStatesLoading}
                options={
                  isStatesLoading
                    ? []
                    : states?.map((state) => ({
                        label: state.description,
                        value: state.description,
                      }))
                }
                placeholder="Please select"
              />
            </div>

            <div className="w-full lg:w-1/2">
              {/*  */}
              <CustomInput
                label="Zip Code"
                name="zip"
              />
              <CustomInput
                label="Phone"
                name="phone"
                required
                maxLength={15}
              />
              <CustomInput
                label="Display Name"
                name="displayName"
                rules={[
                  { required: true, message: "Please enter this field" },
                  {
                    max: 50,
                    message: "Display name cannot exceed 50 characters.",
                  },
                ]}
              />
              {/* ---- Tax Office ---- */}
              <CustomSelect
                label="Tax Office"
                name="taxOffice"
                isLoading={isTaxOfficesLoading}
                options={
                  isTaxOfficesLoading
                    ? []
                    : taxOffices?.map((state) => ({
                        label: state.description,
                        value: state.description,
                      }))
                }
                placeholder="Please select"
              />
              <CustomInput
                label="Tax ID"
                name={"taxId"}
              />
              <CustomSelect
                label="NHF Location"
                name="housingLocation"
                isLoading={isHousingLocationsLoading}
                options={
                  isHousingLocationsLoading
                    ? []
                    : housingLocations?.map((state) => ({
                        label: state.description,
                        value: state.description,
                      }))
                }
                placeholder="Please select"
              />
            </div>
          </div>
        </div>
      </FormSectionWrapper>
    </DashboardContainer>
  )
}

export default EditWorkplaceForm
